import {
  useCreateWorkspaceMutation,
  GetUserSettingsDocument,
  GetWorkspacesDocument,
  LicenseRedemptionPreviewFragment,
} from '@sketch/gql-types'

import { WorkspaceCreationState } from '../../types'
import { formatMutationInput, CreateWorkspaceResponse } from './utils'
import { ErrorHandler } from '@sketch/tracing'
import { useRequestLicenseRedemptions } from '../useRequestLicenseRedemptions'

export interface UseCreateWorkspaceProps {
  onComplete: (workspace: CreateWorkspaceResponse) => void
  onError: (error: string) => void
  onLicensesError: (error: string) => void
  licenses: LicenseRedemptionPreviewFragment[]
}

export const useCreateWorkspace = (props: UseCreateWorkspaceProps) => {
  const { licenses, onComplete, onError, onLicensesError } = props
  const [requestLicenseRedemptions] = useRequestLicenseRedemptions({
    onError: onLicensesError,
  })

  const [createWorkspaceMutation, ...mutation] = useCreateWorkspaceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GetWorkspacesDocument },
      { query: GetUserSettingsDocument },
    ],
    onCompleted: data => {
      if (licenses && licenses.length > 0) {
        const customerId = data.createWorkspace?.workspace?.customer?.identifier

        if (!customerId) {
          ErrorHandler.shouldNeverHappen(
            `There should be always a customerId returned after creating a workspace`
          )
          return
        }
        requestLicenseRedemptions(
          customerId,
          licenses.map(x => x.serial)
        )
      }

      onComplete(data.createWorkspace.workspace)
    },
    onError: ({ message }) => {
      onError(message)
    },
  })

  const createWorkspace = (state: WorkspaceCreationState) =>
    createWorkspaceMutation({
      variables: { input: formatMutationInput(state) },
    })

  return [createWorkspace, ...mutation] as const
}
